// WhatsApp Business Calling — sessão WebRTC no navegador (Fase 2c, Kairo
// 10/07/2026). A Meta é ICE-LITE: o navegador é o agente ICE completo e a
// mídia (opus) flui browser <-> Meta direto. Como a Meta espera SDP COMPLETO
// (sem trickle), a gente espera o ICE gathering terminar antes de devolver o
// SDP. getUserMedia exige HTTPS + gesto do usuário (o clique em Atender/Ligar).
//
// Logs no console prefixados [WACALL] pra depurar mídia (ICE/DTLS) ao vivo.
/* global axios */
const FALLBACK_ICE = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun.cloudflare.com:3478' },
];
const ICE_WAIT_MS = 5000;

const log = (...a) => {
  // eslint-disable-next-line no-console
  console.log('[WACALL]', ...a);
};

// Busca STUN + TURN (coturn do 167) do backend — TURN é o relay que fura o NAT
// quando a mídia direta browser<->Meta não conecta. Credenciais via ENV no
// servidor (trocáveis sem rebuild). Cai no STUN público se falhar.
let iceCache = null;
const fetchIceServers = async () => {
  if (iceCache) return iceCache;
  try {
    const path = window.location.pathname.match(/\/accounts\/(\d+)/);
    const accountId = path ? path[1] : null;
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId}/whatsapp_calls/ice_servers`
    );
    iceCache = data.ice_servers?.length ? data.ice_servers : FALLBACK_ICE;
  } catch (e) {
    log('ice_servers fallback (STUN):', e?.message);
    iceCache = FALLBACK_ICE;
  }
  log('ICE servers:', iceCache.map(s => s.urls));
  return iceCache;
};

export function createCallSession() {
  let pc = null;
  let localStream = null;
  let remoteAudio = null;

  const waitIceComplete = peer =>
    new Promise(resolve => {
      if (peer.iceGatheringState === 'complete') {
        resolve();
        return;
      }
      let settled = false;
      const finish = why => {
        if (settled) return;
        settled = true;
        peer.removeEventListener('icegatheringstatechange', onChange);
        log('ICE gathering pronto:', why, 'state=', peer.iceGatheringState);
        resolve();
      };
      const onChange = () => {
        if (peer.iceGatheringState === 'complete') finish('complete');
      };
      peer.addEventListener('icegatheringstatechange', onChange);
      // rede de segurança: com ICE-lite do outro lado, os candidatos host/srflx
      // que já temos costumam bastar; não trava esperando relay.
      setTimeout(() => finish('timeout'), ICE_WAIT_MS);
    });

  const setup = async () => {
    const iceServers = await fetchIceServers();
    // iceTransportPolicy 'all' tenta direto (host/srflx) e cai no relay (TURN)
    pc = new RTCPeerConnection({ iceServers, iceTransportPolicy: 'all' });
    // diagnósticos de mídia
    pc.oniceconnectionstatechange = () =>
      log('iceConnectionState=', pc.iceConnectionState);
    pc.onconnectionstatechange = () =>
      log('connectionState=', pc.connectionState);
    pc.onicecandidateerror = e => log('icecandidateerror', e.errorText || e);

    localStream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: false,
    });
    localStream.getTracks().forEach(track => pc.addTrack(track, localStream));

    pc.ontrack = event => {
      log('ontrack — faixa remota recebida');
      if (!remoteAudio) {
        remoteAudio = document.createElement('audio');
        remoteAudio.autoplay = true;
        remoteAudio.setAttribute('playsinline', '');
        remoteAudio.style.display = 'none';
        // ANEXA no DOM — <audio> solto não toca de forma confiável
        document.body.appendChild(remoteAudio);
      }
      // eslint-disable-next-line prefer-destructuring
      remoteAudio.srcObject = event.streams[0];
      remoteAudio.play().catch(err => log('play() bloqueado:', err?.message));
    };
    // expõe pra depurar no console: window.__wacall.iceConnectionState
    window.__wacall = pc;
  };

  // INBOUND: recebe o SDP offer da Meta e devolve o nosso SDP answer.
  const answerToOffer = async offerSdp => {
    await setup();
    await pc.setRemoteDescription({ type: 'offer', sdp: offerSdp });
    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);
    await waitIceComplete(pc);
    log('answer SDP pronto');
    return pc.localDescription.sdp;
  };

  // OUTBOUND: cria o SDP offer nosso (a Meta responde com answer via webhook).
  const createOffer = async () => {
    await setup();
    const offer = await pc.createOffer({ offerToReceiveAudio: true });
    await pc.setLocalDescription(offer);
    await waitIceComplete(pc);
    log('offer SDP pronto');
    return pc.localDescription.sdp;
  };

  // OUTBOUND: aplica o SDP answer que a Meta devolveu.
  const applyRemoteAnswer = async answerSdp => {
    if (!pc) return;
    await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
    log('remote answer aplicado');
  };

  const hangup = () => {
    log('hangup');
    try {
      if (localStream) localStream.getTracks().forEach(t => t.stop());
    } catch (e) {
      /* noop */
    }
    try {
      if (pc) pc.close();
    } catch (e) {
      /* noop */
    }
    if (remoteAudio) {
      remoteAudio.srcObject = null;
      remoteAudio.remove();
      remoteAudio = null;
    }
    pc = null;
    localStream = null;
  };

  return { answerToOffer, createOffer, applyRemoteAnswer, hangup };
}
